import { AuthEnums, FileEnums } from "models/enums";
import useAPIClientHelper from "./useAPIClientHelper";
import {
  WintSuperkollAPIModelWintSalary,
  WintSuperkollAPIModelWintSalaryReport,
  WintUtilsWebApi,
} from "wint-api-client/dist/types";
import { SalaryFormTypes } from "models/formTypes";
import { DateHelper } from "utils/helpers";
import { AxiosResponse } from "axios";
import { WintSalaryReportListItem } from "models/propTypes/wintSalaryPropTypes";
import { IconDefinitionConstants, ServiceConstants } from "utils/constants";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";

function useWintSalaryService() {
  const { apiClientInstance } = useAPIClientHelper();

  const approveSalaryMonth = (reportData: WintSuperkollAPIModelWintSalaryReport.SalaryApprovalData) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).WintSalaryClient.ApproveSalaryMonth(reportData);
  };

  const getDueMonthSalaryApprovalReport = () => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).WintSalaryClient.GetDueMonthSalaryApprovalReport();
  };

  const filter = (filter: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).WintSalaryClient.SalaryReport(filter);
  };

  const getPersonSalaryReport = async (personId: number, yearMonth: number) => {
    const response = await apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).WintSalaryClient.GetPersonSalaryReport(personId, yearMonth);
    return response.data;
  };

  const getMinimumTargetGrossSalaryAmount = async (personId: number, yearMonth: number) => {
    const response = await apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).WintSalaryClient.GetMinimumTargetGrossSalaryAmount(personId, yearMonth);
    return response.data;
  };

  const calculateGrossSalaryWithTarget = async (
    personId: number,
    yearMonth: number,
    targetGrossSalaryAmount: number,
    useOneTimeTax?: boolean
  ) => {
    const response = await apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).WintSalaryClient.CalculateGrossSalaryWithTarget(personId, yearMonth, targetGrossSalaryAmount, useOneTimeTax);
    return response.data;
  };

  const updateTargetGrossSalaryAmountApi = async (
    personId: number,
    yearMonth: number,
    request: WintSuperkollAPIModelWintSalary.TargetGrossSalaryUpdateRequest
  ) => {
    const response = await apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).WintSalaryClient.UpdateTargetGrossSalaryAmount(personId, yearMonth, request);
    return response.data;
  };

  const filterSalaryReport = (
    baseFilter: WintUtilsWebApi.Filter,
    formFilter: SalaryFormTypes.WintSalaryReportFilter
  ) => {
    return filter({
      ...baseFilter,
      ...reportToApiFilter(formFilter),
    });
  };

  const reportToApiFilter = (
    filter: SalaryFormTypes.WintSalaryReportFilter
  ): Omit<WintSuperkollAPIModelWintSalaryReport.WintSalaryReportFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      startPeriod: filter.yearMonthRange?.start ? DateHelper.getYearMonth(filter.yearMonthRange?.start) : undefined,
      endPeriod: filter.yearMonthRange?.end ? DateHelper.getYearMonth(filter.yearMonthRange?.end) : undefined,
      personIds: filter.persons?.filter((person) => person.id !== undefined).map((person) => Number(person.id)),
      salaryStates: filter.salaryStates?.map(
        (state) => state.id as WintSuperkollAPIModelWintSalaryReport.WintSalaryState
      ),
      paymentStates: filter.paymentStates?.map(
        (state) => state.id as WintSuperkollAPIModelWintSalaryReport.SalaryPaymentState
      ),
      employeeName: filter.personSearchName || undefined,
    };
  };

  const convertReportToListItems = (
    reportListResponse: AxiosResponse<
      WintUtilsWebApi.ListResponse<WintSuperkollAPIModelWintSalaryReport.WintSalaryReportListItem>
    >
  ): AxiosResponse<WintUtilsWebApi.ListResponse<WintSalaryReportListItem>> => {
    const { data, ...rest } = reportListResponse;
    return {
      ...rest,
      data: {
        ...data,
        items: data.items?.map((r) => ({ ...r, moreActionColumn: null })),
        summaryItem: data.summaryItem ? { ...data.summaryItem, moreActionColumn: null } : undefined,
      },
    };
  };

  const searchPersonsAutoComplete = async (
    searchStr: string,
    excludeSelf?: boolean,
    excludeSalaryInactive?: boolean,
    excludeNotInBusinessUnitWithSalaryRights?: boolean
  ) => {
    const res = await apiClientInstance(AuthEnums.AuthType.Company).WintSalaryClient.SearchPersons({
      ...ServiceConstants.DefaultFilter,
      searchStr: searchStr,
      excludeSelf: excludeSelf ?? true,
      excludeSalaryInactive: excludeSalaryInactive ?? false,
      excludeNotInBusinessUnitWithSalaryRights: excludeNotInBusinessUnitWithSalaryRights ?? false,
    });
    const result = res.data.items ? res.data.items : [];
    return result.map(
      (employee): AutocompleteListItem => ({
        id: employee.id,
        value: employee.name ?? "",
        icon: IconDefinitionConstants.WUser,
      })
    );
  };

  const getPayslipImageDataById = async (payslipId: number): Promise<ImageMeta> => {
    const response = await apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SalarySpecificationDocumentClient.GetById(payslipId);
    const payslip = response.data;
    return {
      id: "",
      contentType: payslip.contentType ?? FileEnums.ContentTypes.ApplicationPDF,
      numberOfPages: 1,
      fileName: payslip.fileName ?? `payslip_${payslipId}.pdf`,
      data: payslip.data,
    };
  };

  const updateTargetGrossSalaryAmount = async (
    personId: number,
    yearMonth: number,
    targetGrossSalaryAmount: number,
    useOneTimeTax?: boolean
  ) => {
    const request: WintSuperkollAPIModelWintSalary.TargetGrossSalaryUpdateRequest = {
      targetGrossSalaryAmount: targetGrossSalaryAmount,
      useOneTimeTax: useOneTimeTax ?? false,
    };
    return updateTargetGrossSalaryAmountApi(personId, yearMonth, request);
  };

  /** showErrors is off: the end-date warning is advisory, so a failed lookup just shows no warning. */
  const getIsConcurrentVacationYear = async (personId: number, endDate: string) => {
    const response = await apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: false,
    }).WintSalaryClient.GetVacationYear(personId, endDate);
    return response.data.isConcurrent;
  };

  return {
    getDueMonthSalaryApprovalReport,
    approveSalaryMonth,
    filterSalaryReport,
    convertReportToListItems,
    reportToApiFilter,
    searchPersonsAutoComplete,
    getPayslipImageDataById,
    getPersonSalaryReport,
    getMinimumTargetGrossSalaryAmount,
    calculateGrossSalaryWithTarget,
    updateTargetGrossSalaryAmount,
    getIsConcurrentVacationYear,
  };
}

export default useWintSalaryService;
